import React, { useMemo, useEffect, useRef, useState } from 'react';
import { omit, isEqual } from 'lodash-es';
import HeaderCards from './components/HeaderCards';
import CardBreakdown from './components/CardBreakdown';
import { getDemographicsNew } from '@/pages/intelligence/services/topcharts';
import { getDemographicsCountryFilters } from '@intelligence/services/filter';
import moment from 'moment';
import {
  ageMap,
  maleMap,
  PAGE_TYPE_GENDER,
  PAGE_TYPE_AGE,
  SOURCE_LIST,
  ST_AGE_ORDER,
  APPANNIE_AGE_ORDER_3,
  APPANNIE_AGE_ORDER_4,
  cardMetricMap,
} from './_config/constant';
import {
  transValueFormatGrowth,
  ageAddPreText,
  isDemographicsDetailDataEmpty,
  getQuarterDateRange,
} from './_config/utils';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { buriedPointGame } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { useDispatch, useIntl, getLocale, history } from 'umi';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { getDbiFilterParams } from '@intelligence/utils/filterUtils';
import {
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_FILTER_KEY,
  URL_SHORT_LINK_NAME,
  URL_APP_IDS,
} from '@intelligence/utils/urlPathCfg';
import { useMobileDetailShortLink } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Stats/_hook';
import { addGlobalCountryCode } from '@intelligence/v3/utils/region';
import { validateDemographicsSource } from '@/pages/intelligence/utils/validateDemographicsSource';

const getRangeTimeGranularity = (dataSource: string, rangeTimeFilterValue?: any) => {
  if (dataSource === 'sensortower') {
    return rangeTimeFilterValue?.type || 'lifetime';
  }

  return rangeTimeFilterValue?.granularity || 'monthly';
};

const normalizeDemographicsFilterTimeType = (granularity?: string) => {
  if (granularity === 'quarterly') {
    return 'quarter';
  }
  if (granularity === 'lifetime') {
    return 'lifetime';
  }
  return granularity || 'month';
};

export default function Demographics(props: any) {
  const {
    basicInfo = {},
    loading: isBasicLoading,
    demographicsIdsList,
    demographicsIdsLoaded,
    appIdList,
    gameId,
    allMarkets,
    filterLoading,
    filterCfg,
    activeAppid,
    shortLinkValidQuery,
    shortLinkFilterValue,
    urlCustomParams,
    appIds,
    onDatasourceChange,
    onFilterChange,
  } = props;
  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();

  const { hasUrlShortLink, defaultValue: DEFAULT_FORM_VALUE } = filterCfg || {};

  const dispatch = useDispatch();
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});
  const genderDefaultIcon = 'pie';
  const ageDefaultIcon = 'line';
  const downloadAppIds = useMemo(
    () => (Array.isArray(appIdList) ? appIdList : []).map((item: any) => item?.id).filter(Boolean),
    [appIdList],
  );
  const [genderIconType, setGenderIconType] = useState(genderDefaultIcon);
  const [ageIconType, setAgeIconType] = useState(ageDefaultIcon);
  const [loading, setLoading] = useState(true);
  const [metricData, setMetricData] = useState<any[]>([]);
  const [genderTrendList, setGenderTrendList] = useState<any[]>([]);
  const [ageTrendList, setAgeTrendList] = useState<any[]>([]);
  const [ageTableHeaderList, setAgeTableHeaderList] = useState<Array<{ text: string; value: string }>>([]);
  const [dateType, setDateType] = useState('Month'); // 只有月粒度
  const [fetchParams, setFetchParams] = useState<any>({
    source: SOURCE_LIST.SENSORTOWER,
    app_ids: [], // 给单个appid
    markets: [], // 国家筛选器
    start_date: '',
    end_date: '',
  });
  const [filterValue, setFilterVale] = useState(isOperateDetail ? detailFilterExtend : DEFAULT_FORM_VALUE);
  const [downloadSearchForm, setdownloadSearchForm] = useState<any>({
    app_ids: downloadAppIds, // app 下拉可选项的 id 数组
    markets: [], // 全量国家，不根据筛选器变化
    start_date: '1970-01-01', // 下载所有时间
    end_date: moment().format('YYYY-MM-DD'),
    file_name: basicInfo?.name, // 游戏名称
    new_style_donwload_file_name: `${basicInfo?.name}_Demographics`,
    datasource: SOURCE_LIST.SENSORTOWER,
    granularity: getRangeTimeGranularity(SOURCE_LIST.SENSORTOWER, filterValue?.[filterKeyMap.RANGE_TIME]),
  });

  const downloadSearchFormNew = useMemo(() => {
    const nextForm = { ...downloadSearchForm, app_ids: downloadAppIds };
    if (nextForm?.datasource === 'sensortower') {
      return nextForm;
    }

    return omit(nextForm, ['datasource', 'granularity']);
  }, [downloadSearchForm, downloadAppIds]);

  useEffect(() => {
    if (!downloadAppIds?.length) return;
    setdownloadSearchForm((prev: any) => {
      if (isEqual(prev?.app_ids, downloadAppIds)) return prev;
      return {
        ...prev,
        app_ids: downloadAppIds,
      };
    });
  }, [downloadAppIds]);

  const activeSourceRaw = filterValue?.[filterKeyMap.DATASOURCE]?.value || fetchParams?.source;
  const activeSource = Array.isArray(activeSourceRaw) ? activeSourceRaw?.[0] : activeSourceRaw;
  const isSensorTower = activeSource === SOURCE_LIST.SENSORTOWER;
  const rangeTimeTypeForDisplay =
    filterValue?.[filterKeyMap.RANGE_TIME]?.type || (isSensorTower ? 'lifetime' : 'month');

  // 记录上一次数据源，用于在 data.ai <-> Sensor Tower 切换时做国家默认值重置
  const prevSourceRef = useRef(filterValue?.[filterKeyMap.DATASOURCE]?.value || SOURCE_LIST.SENSORTOWER);
  const hasAttemptedLifetimeQuarterFallbackRef = useRef(false);
  /** 防止 demographicsIdsList / demographicsIdsLoaded 连续更新触发重复初始化请求 */
  const initFetchKeyRef = useRef('');
  const latestDatesRef = useRef<{
    lifetime_latest_start_date: string;
    lifetime_latest_end_date: string;
    quarter_latest_date: string;
  }>({
    lifetime_latest_start_date: '',
    lifetime_latest_end_date: '',
    quarter_latest_date: '',
  });

  useEffect(() => {
    hasAttemptedLifetimeQuarterFallbackRef.current = false;
    initFetchKeyRef.current = '';
  }, [gameId]);
  const [genderChartType, setGenderChartType] = useState<string>('pie');
  const [ageChartType, setAgeChartType] = useState<string>('line');

  const handlePageLog = (gameId: string) => {
    const pageLogMes = dbiPageLogMes.mobileGameDetailsDemographics;
    if (gameId) {
      buriedPointGame(pageLogMes, gameId);
    }
    // 每次打点的时候，记录当前打点信息用于详情页头部的feedback参数
    recordPageMesForFeedback(pageLogMes);
  };

  const recordPageMesForFeedback = (logMes: any) => {
    dispatch({
      type: `${DBI_TOPCHARTS}/setActivedPageLogMes`,
      payload: logMes,
    });
  };

  useEffect(() => {
    handlePageLog(gameId);
  }, []);

  /** 1 默认有数据的第一个id为筛选器默认值， 2 国家第一个值为默认值， 3 时间回填 */
  const getData = async (
    params: any,
    updateFilterValues?: any,
    options?: { skipTopcharts?: boolean; tryLifetimeQuarterFallback?: boolean },
  ) => {
    /** appids有数据的列表为空 || 国家筛选器markets全量为空 */
    setGenderIconType(genderDefaultIcon);
    setAgeIconType(ageDefaultIcon);
    if (!params?.app_ids?.length) {
      /** 不请求且数据为Nodata */
      catchNodata();
      return;
    }
    setLoading(true);
    try {
      const isUpdateShortLinkNow = !!params?.start_date && !!params?.end_date;
      if (isUpdateShortLinkNow && hasUrlShortLink) {
        updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues, [URL_APP_IDS]: appIds });
      }
      const sourceRaw = params?.source;
      const source = Array.isArray(sourceRaw) ? sourceRaw?.[0] : sourceRaw;
      const rangeTimeType =
        updateFilterValues?.[filterKeyMap.RANGE_TIME]?.type ||
        (source === SOURCE_LIST.APPANNIE ? 'month' : 'lifetime');

      const stGranularityByTimeType: Record<string, string> = {
        lifetime: 'lifetime',
        quarter: 'quarterly',
      };
      const granularityResolverBySource: Record<string, (timeType: string) => string> = {
        [SOURCE_LIST.APPANNIE]: () => 'monthly',
        [SOURCE_LIST.SENSORTOWER]: (timeType: string) => stGranularityByTimeType[timeType] || 'lifetime',
      };
      const granularity = (granularityResolverBySource[source] || granularityResolverBySource[SOURCE_LIST.SENSORTOWER])(
        rangeTimeType,
      );

      // lifetime 粒度 loading 期间先清空筛选器时间展示，等后端返回后再回填
      if (granularity === 'lifetime' && updateFilterValues) {
        const clearedFilterValue = updateTimeFilterValue({
          filterValue: updateFilterValues,
          timeFilterValues: {
            type: 'lifetime',
            value: ['', ''],
          },
        });
        setFilterVale(clearedFilterValue);
      }

      const { markets, start_date, end_date, ...countryFilterParams } = params || {};
      const validatedSource = validateDemographicsSource(source);
      const demographicsCountryFilters = await getDemographicsCountryFilters({
        ...countryFilterParams,
        source: validatedSource,
        granularity,
      });
      const availableMarkets = Array.isArray(demographicsCountryFilters?.data)
        ? demographicsCountryFilters?.data
            ?.flatMap((item: any) => item?.country_set || [])
            ?.map((item: any) => item?.country_code)
            ?.filter(Boolean)
        : [];
      const requestedMarkets = Array.isArray(markets) ? markets : [];
      const hasAllRequestedMarkets =
        requestedMarkets.length > 0 && requestedMarkets.every((item: string) => availableMarkets.includes(item));
      // 后端 filterDemoGraphicsCountryList 接口已返回完整国家列表（包括 global），统一用 availableMarkets[0] 作为 fallback
      const fallbackMarket = availableMarkets?.[0]
        ? [availableMarkets[0]]
        : [];
      const resolvedMarkets = hasAllRequestedMarkets
        ? requestedMarkets
        : fallbackMarket.length
          ? fallbackMarket
          : requestedMarkets;

      // 仅初始化/切换数据源时调 topcharts 拿 app_id；普通筛选器变更（切换 app/国家/时间）跳过
      let topchartsAppId: string | undefined;
      if (!options?.skipTopcharts) {
        const topchartsRes = await onDatasourceChange?.(source, resolvedMarkets);
        topchartsAppId = topchartsRes?.app_id;
      }

      const resolvedParams = {
        ...params,
        source,
        markets: resolvedMarkets,
        // 仅在 topcharts 返回了 app_id 时才覆盖
        ...(topchartsAppId ? { app_ids: [topchartsAppId] } : {}),
        // lifetime 粒度不需要时间范围，清空 start_date/end_date
        ...(granularity === 'lifetime' ? { start_date: '', end_date: '' } : {}),
      };

      // 回填 app 筛选器（仅 topcharts 有返回时）
      if (topchartsAppId && updateFilterValues) {
        updateFilterValues = {
          ...updateFilterValues,
          [filterKeyMap.APP_SELECTOR]: { value: topchartsAppId },
        };
        setFilterVale(updateFilterValues);
      }

      const res = await getDemographicsNew({ ...resolvedParams, granularity });
      const reqData = res?.req || {};
      const { start_date: startDate, end_date: endDate, granularity: resGranularity } = reqData;

      if (reqData.lifetime_latest_start_date) {
        latestDatesRef.current.lifetime_latest_start_date = reqData.lifetime_latest_start_date;
      }
      if (reqData.lifetime_latest_end_date) {
        latestDatesRef.current.lifetime_latest_end_date = reqData.lifetime_latest_end_date;
      }
      if (reqData.quarter_latest_date) {
        latestDatesRef.current.quarter_latest_date = reqData.quarter_latest_date;
      }

      const shouldFallbackLifetimeToQuarter =
        options?.tryLifetimeQuarterFallback === true &&
        !hasAttemptedLifetimeQuarterFallbackRef.current &&
        source === SOURCE_LIST.SENSORTOWER &&
        granularity === 'lifetime' &&
        isDemographicsDetailDataEmpty(res?.data);

      if (shouldFallbackLifetimeToQuarter) {
        hasAttemptedLifetimeQuarterFallbackRef.current = true;
        const quarterAnchor =
          reqData.quarter_latest_date || latestDatesRef.current.quarter_latest_date;
        const quarterRange = quarterAnchor ? getQuarterDateRange(quarterAnchor) : null;
        const quarterFilterBase = updateFilterValues || filterValue;
        const quarterTimeValue = quarterAnchor
          ? [quarterAnchor, quarterAnchor]
          : ['', ''];
        const quarterFilterValues = updateTimeFilterValue({
          filterValue: {
            ...quarterFilterBase,
            [filterKeyMap.RANGE_TIME]: {
              ...(quarterFilterBase?.[filterKeyMap.RANGE_TIME] || {}),
              type: 'quarter',
              value: quarterTimeValue,
            },
          },
          timeFilterValues: {
            type: 'quarter',
            value: quarterTimeValue,
          },
        });
        setFilterVale(quarterFilterValues);
        onFilterChange?.(quarterFilterValues);
        return getData(
          {
            ...resolvedParams,
            granularity: 'quarterly',
            start_date: quarterRange?.start_date || '',
            end_date: quarterRange?.end_date || '',
          },
          quarterFilterValues,
          { skipTopcharts: true },
        );
      }

      if (granularity === 'lifetime' && startDate && endDate) {
        latestDatesRef.current.lifetime_latest_start_date = startDate;
        latestDatesRef.current.lifetime_latest_end_date = endDate;
      }
      // 给个默认时间过去6个月
      const defaultStartTime = moment().subtract(6, 'months').format('YYYY-MM-01');
      const defaultEndTime = moment().format('YYYY-MM-DD');
      setFetchParams({
        ...resolvedParams,
        start_date: startDate || defaultStartTime,
        end_date: endDate || defaultEndTime,
      });
      const nextCountryFilterValue = {
        [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
          value: {
            countries: resolvedMarkets,
            type: 'single',
          },
        },
      };
      const baseFilterValue = {
        ...(updateFilterValues || {}),
        ...nextCountryFilterValue,
      };
      // 获取到时间后更新筛选器的值，并更新url短链接
      // 使用后端返回的 granularity 作为时间筛选器的 type
      const backendTimeType =
        normalizeDemographicsFilterTimeType(resGranularity) ||
        baseFilterValue?.[filterKeyMap.RANGE_TIME]?.type ||
        'month';
      // 只要后端返回了时间，就用后端时间回填筛选器（lifetime/quarter 等模式的时间由后端决定）
      if (startDate && endDate) {
        const newFilterValue = updateTimeFilterValue({
          filterValue: baseFilterValue,
          timeFilterValues: {
            type: backendTimeType,
            value: [startDate, endDate],
          },
        });
        setFilterVale(newFilterValue);
        // 如果是时间为空等时间返回更新 且有短链接功能，则更新短链接
        if (!isUpdateShortLinkNow && hasUrlShortLink) {
          updateShortLinkFilterId({ [URL_FILTER_KEY]: newFilterValue, [URL_APP_IDS]: appIds });
        }
      } else {
        // 后端没返回时间，仅更新 granularity type
        const updatedBaseFilterValue = {
          ...baseFilterValue,
          [filterKeyMap.RANGE_TIME]: {
            ...(baseFilterValue?.[filterKeyMap.RANGE_TIME] || {}),
            type: backendTimeType,
          },
        };
        setFilterVale(updatedBaseFilterValue);
      }
      const sourceNew = source || SOURCE_LIST.SENSORTOWER;
      setdownloadSearchForm({
        ...downloadSearchForm,
        app_ids: downloadAppIds,
        markets: addGlobalCountryCode(allMarkets),
        file_name: basicInfo?.name, // 游戏名称
        new_style_donwload_file_name: `${basicInfo?.name}_Demographics`,
        // 时间是所有时间1970-现在
        // 2025-12-05产品反馈bug，下载的时间跟页面时间对不上，所以算是修改上面下载从1970年~现在的逻辑了
        start_date: startDate,
        end_date: endDate,
        datasource: sourceNew,
        granularity: getRangeTimeGranularity(sourceNew, updateFilterValues?.[filterKeyMap.RANGE_TIME]),
      });

      const _metricData = res?.data?.metrics;

      // 当前接口已统一：Sensor Tower 年龄桶直接返回 <25
      const metricData = _metricData;
      const ageTrendListProcessed = res?.data?.age_trend || [];

      /** Age 折线列表里的 metric 做并集（用于决定表头与展示顺序） */
      const ageTrendKeys: string[] = [];
      ageTrendListProcessed?.forEach((item: any) => {
        item?.metrics?.forEach((t: any) => {
          if (!ageTrendKeys.includes(t?.metric)) {
            ageTrendKeys.push(t?.metric);
          }
        });
      });

      /**
       * 展示卡片内容：后端返回无序，前端按“数据源 + 年龄段定义”有序展示
       * - SensorTower：0-17, 18-24, 25-34, 35-44, 45-54, 55+
       * - data.ai：优先展示拆分桶（25-34/35-44），否则展示聚合桶（25-44）
       */
      const allMetricKeys = Array.from(
        new Set([
          ...((metricData || []).map((t: any) => t?.metric).filter(Boolean) as string[]),
          ...(ageTrendKeys || []),
        ]),
      );

      const appannieHasSplitBuckets =
        allMetricKeys.includes('gte25_lte34') || allMetricKeys.includes('gte35_lte44');

      const ageOrderBySource: Record<string, string[]> = {
        [SOURCE_LIST.SENSORTOWER]: ST_AGE_ORDER,
        [SOURCE_LIST.APPANNIE]: appannieHasSplitBuckets ? APPANNIE_AGE_ORDER_4 : APPANNIE_AGE_ORDER_3,
      };

      const genderOrder = Object.keys(maleMap);
      const ageOrder = ageOrderBySource[source] || ST_AGE_ORDER;
      const cardMetrics = [...genderOrder, ...ageOrder];

      const cardDataList: any[] = [];
      const ageHeaderList: any[] = [];

      cardMetrics.forEach((metric) => {
        /** 获取Metric卡片列表展示及数据 */
        const itemObjMetric = metricData?.find((t: any) => metric === t?.metric);
        if (itemObjMetric) {
          cardDataList.push({
            ...(itemObjMetric || {}),
            value: transValueFormatGrowth(itemObjMetric?.value),
            name: ageMap[metric]
              ? ageAddPreText(ageMap[metric])
              : cardMetricMap[itemObjMetric?.metric] || metric,
          });
        }

        /** 获取Age表格展示个数 */
        const itemObjAge = ageTrendKeys?.find((t) => metric === t);
        if (itemObjAge) {
          /** Age Breakdown表格展示及按cardMetricMap顺序*/
          if (ageMap[metric]) {
            ageHeaderList.push({
              text: ageMap[metric],
              value: metric,
            });
          }
        }
      });
      setAgeTableHeaderList(ageHeaderList);
      setMetricData(cardDataList);
      setGenderTrendList(res?.data?.gender_trend || []);
      setAgeTrendList(ageTrendListProcessed);
      setLoading(false);
    } catch (err) {
      catchNodata();
      setLoading(false);
    }
  };

  const catchNodata = () => {
    setMetricData([]);
    setGenderTrendList([]);
    setAgeTrendList([]);
  };

  const {
    onTableSortChageTrend,
    onTableSortChageEdition,
    // onTableSortChageCountrie,
    updateMobileDetailShortLink,
    updatePageCfgFromShortLink,
    trendPageSortAttr,
    editionPageSortAttr,
    // countriesPageSortAttr,
  } = useMobileDetailShortLink({
    // coutriesType: countryTypeRef.current,
    trendSortMetric: 'date',
    editionSortMetric: 'date',
    // countrieSortMetric: chartSegment,
    shortLinkValidQuery,
    isCoutriesPage: false,
    trendChartType: genderChartType,
    editionChartType: ageChartType,
    setTrendChartType: setGenderChartType,
    setEditionChartType: setAgeChartType,
  });
  useEffect(() => {
    updatePageCfgFromShortLink();
  }, [shortLinkValidQuery]);

  useEffect(() => {
    if (!filterLoading && hasUrlShortLink) {
      updateMobileDetailShortLink();
    }
  }, [filterLoading, ageChartType, genderChartType]);

  // Sensor Tower 数据源下：Gender / Age Breakdown 仅允许饼图
  useEffect(() => {
    if (isSensorTower && genderChartType !== 'pie') {
      setGenderChartType('pie');
    }
    if (isSensorTower && ageChartType !== 'pie') {
      setAgeChartType('pie');
    }
  }, [isSensorTower, ageChartType, genderChartType]);

  useEffect(() => {
    if (!isBasicLoading) {
      if (!demographicsIdsList?.length) {
        if (demographicsIdsLoaded) {
          // getIsDemographics 请求已完成，确实无数据，关闭 loading、清空数据
          setLoading(false);
          setMetricData([]);
          setGenderTrendList([]);
          setAgeTrendList([]);
        }
        return;
      }

      const initFetchKey = `${gameId}|${demographicsIdsList.join(',')}`;
      if (initFetchKeyRef.current === initFetchKey) {
        return;
      }
      initFetchKeyRef.current = initFetchKey;

      (async () => {
        // 初始化开始即进入 loading，覆盖 topcharts / countryList / metrics 全部请求阶段
        setLoading(true);
        const hasUrlShortLinkVal = history.location?.query?.[URL_SHORT_LINK_NAME];
        const { filterValue: extendFilter, filterParams } = getDbiFilterParams({
          // userInfo,
          urlCustomParams,
          urlShortLinkFilter: shortLinkFilterValue,
          // urlFilter: any,
          filterSaveData: null,
          filterExtend: detailFilterExtend,
          specialParams: filterCfg?.specialParams,
          isOperate: isOperateDetail,
          filterName: '',
          filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
        });

        let newFilterValues = { ...DEFAULT_FORM_VALUE, ...extendFilter };

        // 设置继承模式，但将 RANGE_TIME 重置为通用安全值（type: 'date'），
        // 避免 Demographics 特有的时间类型（quarter/lifetime）在 merge 模式下残留在 Redux 中，
        // 导致其他 tab（如 Streaming）继承到不兼容的时间类型而显示"请选择"
        if (isOperateDetail || hasUrlShortLinkVal) {
          const { [filterKeyMap.RANGE_TIME]: _rangeTime, ...restFilterValues } = newFilterValues;
          setDetailFilterExtend({
            ...restFilterValues,
            [filterKeyMap.RANGE_TIME]: { type: 'date', value: ['', ''] },
          });
        }

        const initSourceRaw = newFilterValues?.[filterKeyMap.DATASOURCE]?.value || SOURCE_LIST.SENSORTOWER;
        const initSource = Array.isArray(initSourceRaw) ? initSourceRaw?.[0] : initSourceRaw;
        prevSourceRef.current = initSource;

        let newFetchParams = {
          ...fetchParams,
          source: initSource,
          app_ids: [activeAppid],
        };

        setFilterVale(newFilterValues);
        onFilterChange?.(newFilterValues);

        const filterApiParams = filterCfg?.handleFilterToApiParams(filterParams) || {};
        newFetchParams = { ...newFetchParams, ...filterApiParams };
        // filterApiParams 可能包含旧数据源的 app_ids/source，用正确值覆盖回来
        newFetchParams.app_ids = [activeAppid];
        newFetchParams.source = initSource;

        // 清空 markets 和时间，让 getData 根据 filterDemoGraphicsCountryList 接口返回的国家列表 fallback，时间由后端决定
        newFetchParams = { ...newFetchParams, start_date: '', end_date: '' };
        // getData 内部会先调 getDemographicsCountryFilters，再调 topcharts（携带 markets），最后调 metrics
        getData(newFetchParams, newFilterValues, { tryLifetimeQuarterFallback: true });
      })();
    }
  }, [isBasicLoading, demographicsIdsList, demographicsIdsLoaded]);

  const filterConfirm = async (value: any, filter: any, myFilterValue: any) => {
    // 筛选器确认后立即进入 loading，覆盖 topcharts / countryList / metrics 全部请求阶段
    setLoading(true);
    const nextSourceRaw = value?.source || fetchParams?.source;
    const nextSource = Array.isArray(nextSourceRaw) ? nextSourceRaw?.[0] : nextSourceRaw;
    const prevSource = prevSourceRef.current;

    // 切换数据源时同步 Gender/Age Breakdown 默认图表类型
    if (prevSource !== nextSource) {
      if (nextSource === SOURCE_LIST.SENSORTOWER) {
        setGenderChartType('pie');
      }
      setAgeChartType(nextSource === SOURCE_LIST.SENSORTOWER ? 'pie' : 'line');
    }

    let nextApiParams = value;
    let nextFilterValues = filter;

    // 切换数据源时：清空 markets，让 getData 内部根据 filterDemoGraphicsCountryList 接口返回的国家列表自动 fallback
    if (prevSource !== nextSource) {
      nextApiParams = { ...value, source: nextSource, markets: [] };
    }

    const isSourceChanged = prevSourceRef.current !== nextSource;

    if (isSourceChanged) {
      // 切源后同步时间筛选器的展示类型：data.ai 用 month；Sensor Tower 用 lifetime
      const nextTimeType = nextSource === SOURCE_LIST.APPANNIE ? 'month' : 'lifetime';
      nextFilterValues = {
        ...nextFilterValues,
        [filterKeyMap.RANGE_TIME]: {
          ...(nextFilterValues?.[filterKeyMap.RANGE_TIME] || { value: ['', ''] }),
          type: nextTimeType,
        },
      };
      setFilterVale(nextFilterValues);

      prevSourceRef.current = nextSource;
    }

    // 修正 Quarter 粒度：后端仍然只认 start_date/end_date（YYYY-MM-DD），这里以筛选器值为准覆盖 apiParams
    const rangeTime = nextFilterValues?.[filterKeyMap.RANGE_TIME];
    const rangeTimeType = rangeTime?.type;
    const rangeTimeValue = rangeTime?.value;
    if (rangeTimeType === 'quarter' && Array.isArray(rangeTimeValue)) {
      const start = rangeTimeValue?.[0];
      const end = rangeTimeValue?.[1] || rangeTimeValue?.[0];
      const startM = moment(start);
      const endM = moment(end);
      if (startM.isValid() && endM.isValid()) {
        nextApiParams = {
          ...nextApiParams,
          start_date: startM.startOf('quarter').format('YYYY-MM-DD'),
          end_date: endM.endOf('quarter').format('YYYY-MM-DD'),
        };
      }
    }

    onFilterChange?.(nextFilterValues);
    // 切换数据源或切换国家时需要调 topcharts；仅切换 app/时间时跳过
    const nextMarkets = nextApiParams?.markets;
    const prevMarkets = fetchParams?.markets;
    const isMarketsChanged = !isEqual(
      Array.isArray(nextMarkets) ? nextMarkets : [],
      Array.isArray(prevMarkets) ? prevMarkets : [],
    );
    const needTopcharts = isSourceChanged || isMarketsChanged;
    getData({ ...fetchParams, ...nextApiParams }, nextFilterValues, {
      skipTopcharts: !needTopcharts,
    });
  };

  const getGenderChartIconType = (type: string) => {
    setGenderIconType(type);
  };
  const getAgeChartIconType = (type: string) => {
    setAgeIconType(type);
  };

  // console.log('fetchParamsfetchParams',fetchParams)

  return (
    <div className="dbi-demographics-page-container">
      <div className="dbi-statsfilter-warp">
        <div className="statsfilter-warp-left">
          <Filter
            defaultValue={DEFAULT_FORM_VALUE}
            filterCfg={filterCfg}
            // timeFilterValues={{
            //   type: 'monthly',
            //   value: [fetchParams.start_date, fetchParams.end_date],
            // }}
            filterConfirm={filterConfirm}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
            isNewFilterExtend={true}
          />
        </div>

        <div className="dbi-statsfilter-warp-right">
          <ExportTable
            entityType="mobile"
            tableParams={downloadSearchFormNew}
            immediateDownload={true} // 是否直接下载不弹窗
            downloadLogConfigKey={'TopChartsMobileGamesDetailDemographicsDownloadButton'}
            businessType="mobile-detail-demographics"
            isDisableDownload={loading}
            downloadModalSize={820}
            isDisableDataAmount={true}
          />
        </div>
      </div>


      <div className="dbi-detail-card-row dbi-detail-card-demographics">
        {metricData.map((item, index) => <HeaderCards data={item} key={index} />)}
      </div>
      <CardBreakdown
        pageType={PAGE_TYPE_GENDER}
        // showEchartIcon={false}
        defaultChartIcon={genderIconType}
        getChartIconType={getGenderChartIconType}
        showMaleEchart={true}
        showSegement={false}
        // 请求状态
        loading={isBasicLoading || loading}
        // 折线图数据
        lineData={genderTrendList}
        // 表格数据，
        tableData={genderTrendList}
        // 柱状图数据
        barOrPieData={metricData}
        // 用于表头指标渲染
        tableHeaderOptions={[
          { text: formatMessage({ id: `dbi_top_male` }), value: 'male' },
          { text: formatMessage({ id: `dbi_top_female` }), value: 'female' },
        ]}
        cardTitle={`${formatMessage({ id: `dbi_top_gender_breakdown` })}(%)`}
        chartCardTitle={
          locale === LANGUAGE_CN
            ? `${formatMessage({ id: `dbi_top_gender_breakdown` })}`
            : 'Gender\nBreakdown(%)'
        }
        dateType={dateType}
        cardStatus={{
          isLoading: isBasicLoading || loading,
          // isError: editionError,
          isNoData: !genderTrendList?.length,
        }}
        startDate={fetchParams?.start_date}
        endDate={fetchParams?.end_date}
        onTableSortChage={onTableSortChageTrend}
        pageSortAttr={trendPageSortAttr}
        chartType={genderChartType}
        setChartType={setGenderChartType}
        disableChartType={isSensorTower ? ['bar', 'line'] : undefined}
        rangeTimeType={rangeTimeTypeForDisplay}
        disableSort={isSensorTower}
      />
      <CardBreakdown
        defaultChartIcon={ageIconType}
        getChartIconType={getAgeChartIconType}
        pageType={PAGE_TYPE_AGE}
        showMaleEchart={false}
        // showEchartIcon={false}
        showSegement={false}
        // 请求状态
        loading={isBasicLoading || loading}
        // 折线图数据
        lineData={ageTrendList}
        // 表格数据，
        tableData={ageTrendList}
        // 柱状图数据
        barOrPieData={metricData}
        // 用于表头指标渲染
        tableHeaderOptions={ageTableHeaderList}
        cardTitle={`${formatMessage({ id: `dbi_top_age_breakdown` })}(%)`}
        chartCardTitle={
          locale === LANGUAGE_CN
            ? `${formatMessage({ id: `dbi_top_age_breakdown` })}`
            : 'Age\nBreakdown(%)'
        }
        dateType={dateType}
        startDate={fetchParams?.start_date}
        endDate={fetchParams?.end_date}
        fetchParams={fetchParams}
        cardStatus={{
          isLoading: isBasicLoading || loading,
          // isError: editionError,
          isNoData: !ageTrendList?.length,
        }}
        pageSortAttr={editionPageSortAttr}
        onTableSortChage={onTableSortChageEdition}
        chartType={ageChartType}
        setChartType={setAgeChartType}
        disableChartType={isSensorTower ? ['bar', 'line'] : undefined}
        rangeTimeType={rangeTimeTypeForDisplay}
        disableSort={isSensorTower}
      />
    </div>
  );
}
